import api from './api'
import type { ApiResponse, PaginatedResponse } from '../types'

// ================================================================================
// CONTACT SERVICE — public contact form + admin inbox
// Public submit POST /contact; admin inbox GET/PUT/DELETE (ADMIN/SUPER_ADMIN).
// ================================================================================

export type ContactPurpose =
  | 'GENERAL_ENQUIRY'
  | 'NEED_HELP_TB'
  | 'BECOME_NIKSHAY_MITRA'
  | 'VOLUNTEER_NGO'
  | 'MEDIA_PRESS'
  | 'SHARE_STORY'
  | 'FEEDBACK'

export type ContactStatus = 'NEW' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED'

export interface ContactMessage {
  id: number
  name: string
  email: string
  phone?: string
  purpose: string
  message: string
  status: ContactStatus
  adminNotes?: string
  createdAt: string
  updatedAt?: string
  respondedAt?: string
}

export interface ContactSubmitRequest {
  name: string
  email: string
  phone?: string
  purpose: ContactPurpose
  message: string
}

/** POST /contact — public submission. */
export async function submitContact(req: ContactSubmitRequest): Promise<ContactMessage> {
  const res = await api.post<ApiResponse<ContactMessage>>('/contact', req)
  return res.data.data
}

// ---- admin inbox (ADMIN / SUPER_ADMIN) ----

export async function getContactMessages(params: {
  status?: string
  search?: string
  page?: number
  size?: number
}): Promise<PaginatedResponse<ContactMessage>> {
  const qs = new URLSearchParams()
  if (params.status) qs.set('status', params.status)
  if (params.search) qs.set('search', params.search)
  qs.set('page', String(params.page ?? 0))
  qs.set('size', String(params.size ?? 20))
  const res = await api.get<ApiResponse<PaginatedResponse<ContactMessage>>>(`/contact?${qs.toString()}`)
  return res.data.data
}

export async function getContactStats(): Promise<Record<string, number>> {
  const res = await api.get<ApiResponse<Record<string, number>>>('/contact/stats')
  return res.data.data
}

export async function updateContactStatus(
  id: number,
  status: ContactStatus,
  notes?: string,
): Promise<ContactMessage> {
  const qs = new URLSearchParams({ status })
  if (notes !== undefined) qs.set('notes', notes)
  const res = await api.put<ApiResponse<ContactMessage>>(`/contact/${id}/status?${qs.toString()}`)
  return res.data.data
}

export async function deleteContactMessage(id: number): Promise<void> {
  await api.delete<ApiResponse<void>>(`/contact/${id}`)
}

export const PURPOSE_LABELS: Record<ContactPurpose, string> = {
  GENERAL_ENQUIRY: 'General enquiry',
  NEED_HELP_TB: 'I need help with TB',
  BECOME_NIKSHAY_MITRA: 'Become a TB Supporter (Ni-kshay Mitra)',
  VOLUNTEER_NGO: 'Volunteer / NGO',
  MEDIA_PRESS: 'Media / press',
  SHARE_STORY: 'Share a story',
  FEEDBACK: 'Feedback',
}

export const STATUS_LABELS: Record<ContactStatus, string> = {
  NEW: 'New',
  IN_PROGRESS: 'In progress',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed',
}